/**
 * TenantDatabaseCard — gdzie żyje baza firmy (tryb dedicated) i jej stan.
 *
 * Baza na serwerze klienta: admin z uprawnieniem db_servers.manage obsługuje serwer stąd
 * (sprawdź połączenie, edytuj, instrukcja wdrożenia) — osobnego ekranu serwerów nie ma.
 *
 * Gdy baza czeka na aktualizację ('outdated'), admin z uprawnieniem db_servers.manage
 * może zlecić „Ponów aktualizację”. Ostatnie zlecenie odświeżamy co 10 s, dopóki jest
 * w kolejce / w toku; po zakończeniu odświeżamy szczegóły tenanta.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DbServerChecksList } from '@/components/db-servers/DbServerChecksList';
import { DbServerFormModal } from '@/components/db-servers/DbServerFormModal';
import { InstallGuideModal } from '@/components/db-servers/InstallGuideModal';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  DB_SERVERS_KEY,
  type DbServerCheckResult,
  type DbServerDto,
  useCheckDbServer,
} from '@/lib/db-servers/api';

type JobStatus = 'queued' | 'running' | 'done' | 'failed';

interface UpdateJob {
  id: string;
  status: JobStatus;
  requestedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
}

interface JobResponse {
  job: UpdateJob | null;
}

export interface TenantDatabaseCardProps {
  tenantId: string;
  dbStatus: string;
  dbServer: { id: string; name: string; host: string | null; status: string } | null;
}

const JOB_COLOR: Record<JobStatus, BadgeColor> = {
  queued: 'gray',
  running: 'blue',
  done: 'green',
  failed: 'red',
};

const JOB_KEY: Record<JobStatus, string> = {
  queued: 'admin.tenants.detail.database.jobQueued',
  running: 'admin.tenants.detail.database.jobRunning',
  done: 'admin.tenants.detail.database.jobDone',
  failed: 'admin.tenants.detail.database.jobFailed',
};

const STATUS_KEY: Record<string, string> = {
  active: 'admin.tenants.detail.database.statusActive',
  outdated: 'admin.tenants.detail.database.statusOutdated',
  suspended: 'admin.tenants.detail.database.statusSuspended',
  provisioning: 'admin.tenants.detail.database.statusProvisioning',
  failed: 'admin.tenants.detail.database.statusFailed',
  migrating: 'admin.tenants.detail.database.statusMigrating',
};

const RETRY_ERROR_KEY: Record<string, string> = {
  NOT_OUTDATED: 'admin.tenants.detail.database.errNotOutdated',
  JOB_ALREADY_OPEN: 'admin.tenants.detail.database.errJobOpen',
  FORBIDDEN: 'admin.tenants.detail.database.errForbidden',
  TENANT_NOT_FOUND: 'admin.tenants.detail.database.errTenantNotFound',
};

function formatDateTime(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function TenantDatabaseCard({ tenantId, dbStatus, dbServer }: TenantDatabaseCardProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('db_servers.manage');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [checkResult, setCheckResult] = useState<DbServerCheckResult | null>(null);

  const serverKey = [...DB_SERVERS_KEY, 'item', dbServer?.id];
  const { data: serverDto } = useQuery<DbServerDto>({
    enabled: canManage && !!dbServer,
    queryKey: serverKey,
    queryFn: async () =>
      (await api.get<{ item: DbServerDto }>(`/admin/db-servers/${dbServer!.id}`)).data.item,
    retry: false,
  });
  const check = useCheckDbServer();
  const runCheck = () => {
    if (!dbServer) return;
    check.mutate(dbServer.id, {
      onSuccess: (res) => {
        setCheckResult(res.result);
        void queryClient.invalidateQueries({ queryKey: serverKey });
      },
      onError: () =>
        toast.show({ variant: 'error', title: t('admin.tenants.detail.clientDb.errCheck') }),
    });
  };

  const jobKey = ['admin', 'tenant', tenantId, 'retry-update'];
  // Ostatnie zlecenie widzi tylko admin z uprawnieniem — inni nie wywołują endpointu.
  const { data, isError: jobError } = useQuery<JobResponse>({
    queryKey: jobKey,
    queryFn: async () =>
      (await api.get<JobResponse>(`/admin/tenants/${tenantId}/retry-update`)).data,
    enabled: !!tenantId && canManage,
    refetchInterval: (query) => {
      const st = query.state.data?.job?.status;
      return st === 'queued' || st === 'running' ? 10_000 : false;
    },
  });
  const job = data?.job ?? null;

  // Gdy zlecenie przeszło z otwartego w zakończone — odśwież szczegóły tenanta (stan bazy).
  const prevStatusRef = useRef<JobStatus | null>(null);
  useEffect(() => {
    const prev = prevStatusRef.current;
    const cur = job?.status ?? null;
    if ((prev === 'queued' || prev === 'running') && (cur === 'done' || cur === 'failed')) {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] });
    }
    prevStatusRef.current = cur;
  }, [job?.status, queryClient, tenantId]);

  const retry = useMutation({
    mutationFn: async () =>
      (await api.post<JobResponse>(`/admin/tenants/${tenantId}/retry-update`)).data,
    onSuccess: () => {
      setConfirmOpen(false);
      toast.show({ variant: 'success', title: t('admin.tenants.detail.database.retryToast') });
      void queryClient.invalidateQueries({ queryKey: jobKey });
    },
    onError: (err) => {
      setConfirmOpen(false);
      const axErr = err as AxiosError<{ error?: string }>;
      const code = axErr.response?.data?.error;
      const key =
        (code ? RETRY_ERROR_KEY[code] : undefined) ??
        (axErr.response?.status === 403
          ? 'admin.tenants.detail.database.errForbidden'
          : 'admin.tenants.detail.database.errUnknown');
      toast.show({ variant: 'error', title: t(key) });
      if (code === 'JOB_ALREADY_OPEN' || code === 'NOT_OUTDATED') {
        void queryClient.invalidateQueries({ queryKey: jobKey });
        void queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] });
      }
    },
  });

  const statusKey = STATUS_KEY[dbStatus];
  const statusText = statusKey ? t(statusKey) : dbStatus;
  const showRetry = dbStatus === 'outdated' && canManage;

  return (
    <Card padding="md">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">
          {t('admin.tenants.detail.database.title')}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {canManage && dbServer && (
            <>
              <Button variant="secondary" size="sm" onClick={runCheck} loading={check.isPending}>
                {t('admin.tenants.detail.clientDb.check')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditOpen(true)}
                disabled={!serverDto}
              >
                {t('admin.tenants.detail.clientDb.edit')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setGuideOpen(true)}>
                {t('admin.tenants.detail.clientDb.openGuide')}
              </Button>
            </>
          )}
          {showRetry && (
            <Button variant="secondary" size="sm" onClick={() => setConfirmOpen(true)}>
              {t('admin.tenants.detail.database.retryButton')}
            </Button>
          )}
        </div>
      </div>

      <dl className="flex flex-col gap-2 text-sm">
        <div>
          <dt className="text-xs text-text-tertiary">
            {t('admin.tenants.detail.database.location')}
          </dt>
          <dd className="text-text-primary">
            {dbServer ? (
              <>
                {dbServer.name}{' '}
                {dbServer.host && (
                  <span className="text-xs text-text-muted">({dbServer.host})</span>
                )}
              </>
            ) : (
              t('admin.tenants.detail.database.ourServer')
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-text-tertiary">
            {t('admin.tenants.detail.database.status')}
          </dt>
          <dd className="text-text-primary">{statusText}</dd>
        </div>
        {canManage && (
          <div>
            <dt className="text-xs text-text-tertiary">
              {t('admin.tenants.detail.database.lastJob')}
            </dt>
            <dd className="text-text-primary">
              {jobError ? (
                <span className="text-red-600 dark:text-red-400" data-testid="db-card-job-error">
                  {t('admin.tenants.detail.database.jobLoadError')}
                </span>
              ) : job ? (
                <div className="flex flex-col gap-1">
                  <div>
                    <Badge color={JOB_COLOR[job.status]}>{t(JOB_KEY[job.status])}</Badge>
                  </div>
                  <span className="text-xs text-text-muted">
                    {t('admin.tenants.detail.database.requestedAt')}:{' '}
                    {formatDateTime(job.requestedAt, i18n.language)}
                    {' · '}
                    {t('admin.tenants.detail.database.startedAt')}:{' '}
                    {formatDateTime(job.startedAt, i18n.language)}
                    {' · '}
                    {t('admin.tenants.detail.database.finishedAt')}:{' '}
                    {formatDateTime(job.finishedAt, i18n.language)}
                  </span>
                  {job.status === 'failed' && job.error && (
                    <span className="text-xs text-red-600 dark:text-red-400">{job.error}</span>
                  )}
                </div>
              ) : (
                <span className="text-text-muted">{t('admin.tenants.detail.database.noJob')}</span>
              )}
            </dd>
          </div>
        )}
        {canManage && serverDto && (
          <div>
            <dt className="text-xs text-text-tertiary">
              {t('admin.tenants.detail.database.lastCheck')}
            </dt>
            <dd className="text-text-primary">
              {serverDto.lastCheckAt ? (
                <span className="flex items-center gap-2">
                  {formatDateTime(serverDto.lastCheckAt, i18n.language)}
                  <Badge color={serverDto.lastCheckOk ? 'green' : 'red'}>
                    {serverDto.lastCheckOk
                      ? t('admin.tenants.detail.database.checkOk')
                      : t('admin.tenants.detail.database.checkFailed')}
                  </Badge>
                </span>
              ) : (
                <span className="text-text-muted">
                  {t('admin.tenants.detail.database.notChecked')}
                </span>
              )}
            </dd>
          </div>
        )}
      </dl>

      {checkResult && (
        <div className="mt-3">
          <DbServerChecksList result={checkResult} />
        </div>
      )}

      <InstallGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
      <DbServerFormModal
        open={editOpen && !!serverDto}
        server={serverDto ?? null}
        onClose={() => {
          setEditOpen(false);
          void queryClient.invalidateQueries({ queryKey: serverKey });
          void queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] });
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        variant="primary"
        title={t('admin.tenants.detail.database.confirmTitle')}
        message={t('admin.tenants.detail.database.confirmMessage')}
        confirmLabel={t('admin.tenants.detail.database.retryButton')}
        confirming={retry.isPending}
        onConfirm={() => retry.mutate()}
        onClose={() => setConfirmOpen(false)}
      />
    </Card>
  );
}
